import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import assets from "../lib/design_assets.json";
import { scrollToHash } from "../lib/lenis";
import { Link } from "react-router-dom";

const HERO_IMG = assets.media.image_urls.hero_mbfc.url;

const lines = [
  { text: "EXCEPTIONAL", accent: false },
  { text: "LANDED HOMES.", accent: true },
  { text: "EXPERTLY CURATED.", accent: false },
];

const MaskedLine = ({ text, accent, delay }) => {
  const reduce = useReducedMotion();
  return (
    <span className="hero-mask">
      <motion.span
        className={`block font-serif leading-[0.98] tracking-tight ${
          accent ? "italic text-ivory/90" : "text-ivory"
        } text-[13.5vw] sm:text-7xl lg:text-[5.6rem]`}
        initial={reduce ? false : { y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.15, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {text}
      </motion.span>
    </span>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="grain relative flex min-h-[100svh] flex-col overflow-hidden bg-charcoal" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={reduce ? {} : { y: imgY }}>
        <motion.img
          src={HERO_IMG}
          alt="Singapore Marina Bay Financial Centre skyline at night"
          className="h-[115%] w-full object-cover saturate-[0.65]"
          initial={reduce ? false : { scale: 1.12, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-charcoal/75 via-charcoal/45 to-charcoal/90" />

      <motion.div
        style={reduce ? {} : { opacity: fade }}
        className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-6 pt-32 pb-40 lg:px-8"
      >
        <motion.p
          className="eyebrow text-ivory/70"
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.15 }}
          data-testid="hero-eyebrow"
        >
          PNG Landed Excellence · Singapore Landed Property Specialists
        </motion.p>

        <h1 className="mt-8" data-testid="hero-headline">
          {lines.map((l, i) => (
            <MaskedLine key={l.text} text={l.text} accent={l.accent} delay={0.25 + i * 0.16} />
          ))}
        </h1>

        <motion.p
          className="mt-8 max-w-xl text-base leading-relaxed text-ivory/80 sm:text-lg"
          initial={reduce ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-subheading"
        >
          Andy Cheng and the PNG Landed Excellence team curate, market and secure Singapore's
          finest landed homes across all 24 landed districts — with 20+ years of specialist experience.
        </motion.p>

        <motion.div
          className="mt-10 flex flex-wrap items-center gap-4"
          initial={reduce ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
        >
          <Link
            to="/properties"
            data-testid="hero-cta-explore"
            className="group flex items-center gap-3 bg-brand px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-brand-dark"
          >
            Explore Properties
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <button
            type="button"
            onClick={() => scrollToHash("#contact")}
            data-testid="hero-cta-speak-to-andy"
            className="group flex items-center gap-3 border border-ivory/60 px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-ivory transition-colors duration-300 hover:bg-ivory hover:text-charcoal"
          >
            Speak to Andy
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        type="button"
        onClick={() => scrollToHash("#discovery")}
        className="absolute bottom-36 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-ivory/70 transition-colors hover:text-ivory sm:flex"
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        aria-label="Scroll to property search"
        data-testid="hero-scroll-cue"
      >
        <span className="text-[0.65rem] uppercase tracking-[0.3em]">Begin the search</span>
        <ArrowDown className="h-4 w-4 animate-bounce" />
      </motion.button>
    </section>
  );
};
